/** SUNTRONIX ONE — Data Quality: bulk Brand / Vertical product classification. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Wand2, Save, RefreshCw, Search, SlidersHorizontal } from "lucide-react";

export default function ProductClassification() {
  const [rows, setRows] = useState([]);
  const [totalPending, setTotalPending] = useState(0);
  const [brands, setBrands] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [q, setQ] = useState("");
  const [missingOnly, setMissingOnly] = useState(true);
  const [edits, setEdits] = useState({});      // product_id -> {brand, vertical}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (query = q, missing = missingOnly) => {
    try {
      const [{ data: cls }, { data: b }, { data: v }] = await Promise.all([
        api.get("/mb-inventory/classification", { params: { q: query || undefined, missing_only: missing, limit: 200 } }),
        api.get("/mb-inventory/brands"),
        api.get("/mb-inventory/verticals"),
      ]);
      setRows(cls.items || []); setTotalPending(cls.total_pending || 0);
      setBrands(b.items || []); setVerticals((v.items || []).filter((x) => x.status === "ACTIVE"));
      setEdits({});
    } catch { toast.error("Failed to load classification queue"); }
  }, [q, missingOnly]);
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => load(q, missingOnly), 400);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line

  const setEdit = (pid, field, value) =>
    setEdits((e) => ({ ...e, [pid]: { ...e[pid], [field]: value } }));

  const applySuggestion = (r) => {
    const s = r.suggestion || {};
    setEdits((e) => ({ ...e, [r.id]: {
      ...(s.brand ? { brand: s.brand } : {}), ...(s.vertical ? { vertical: s.vertical } : {}), ...e[r.id] } }));
  };

  const save = async () => {
    const assignments = Object.entries(edits)
      .filter(([, v]) => v.brand || v.vertical)
      .map(([product_id, v]) => ({ product_id, ...v }));
    if (!assignments.length) return toast.info("No changes to save");
    setBusy(true);
    try {
      const { data } = await api.post("/mb-inventory/classification/bulk", { assignments });
      toast.success(`${data.updated} products classified`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const autoClassify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mb-inventory/classification/auto", {});
      toast.success(`Auto-assigned ${data.brands_assigned} brands, ${data.verticals_assigned} verticals`);
      load();
    } catch { toast.error("Auto-classify failed"); }
    finally { setBusy(false); }
  };

  const pendingCount = Object.values(edits).filter((v) => v.brand || v.vertical).length;

  return (
    <div className="max-w-[1400px] mx-auto space-y-4" data-testid="product-classification-page">
      <header className="flex items-center gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-lg bg-emerald-900 text-white grid place-items-center"><SlidersHorizontal className="h-5 w-5" /></div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-slate-900">Product Classification</h1>
          <p className="text-xs text-slate-500">{totalPending.toLocaleString()} products awaiting Brand / Vertical assignment</p>
        </div>
        <button onClick={autoClassify} disabled={busy} data-testid="auto-classify-btn"
          className="h-9 px-3 text-sm border border-emerald-700 text-emerald-800 rounded inline-flex items-center gap-1.5 disabled:opacity-50">
          <Wand2 className="h-3.5 w-3.5" /> Auto-classify confident matches</button>
        <button onClick={save} disabled={busy || pendingCount === 0} data-testid="save-classification-btn"
          className="h-9 px-4 text-sm bg-emerald-700 text-white rounded inline-flex items-center gap-1.5 disabled:opacity-50">
          <Save className="h-3.5 w-3.5" /> Save {pendingCount > 0 ? `(${pendingCount})` : ""}</button>
      </header>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q, missingOnly)}
            placeholder="Search product name…" data-testid="classification-search-input"
            className="h-8 pl-7 pr-2 border border-slate-300 rounded text-sm w-72" />
        </div>
        <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
          <input type="checkbox" checked={missingOnly} data-testid="missing-only-toggle"
            onChange={(e) => { setMissingOnly(e.target.checked); load(q, e.target.checked); }} />
          Unclassified only</label>
        <button onClick={() => load()} className="h-8 px-2.5 text-xs border border-slate-300 rounded inline-flex items-center gap-1" data-testid="classification-refresh-btn">
          <RefreshCw className="h-3 w-3" /> Refresh</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-left text-slate-600">
              <th className="px-3 py-2 font-semibold">Product</th>
              <th className="px-3 py-2 font-semibold w-28">Category</th>
              <th className="px-3 py-2 font-semibold w-44">Brand</th>
              <th className="px-3 py-2 font-semibold w-52">Vertical</th>
              <th className="px-3 py-2 font-semibold w-40">Suggestion</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">Nothing pending classification.</td></tr>)}
            {rows.map((r) => {
              const e = edits[r.id] || {};
              const s = r.suggestion || {};
              return (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-emerald-50/30" data-testid={`classify-row-${r.id}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-800">{r.product_name}</td>
                  <td className="px-3 py-1.5 text-slate-500">{r.category || "—"}</td>
                  <td className="px-3 py-1.5">
                    <input list="brand-options" value={e.brand ?? r.brand ?? ""}
                      onChange={(ev) => setEdit(r.id, "brand", ev.target.value)}
                      data-testid={`brand-input-${r.id}`}
                      className={`h-7 px-1.5 w-full text-[11px] border rounded ${e.brand ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} />
                  </td>
                  <td className="px-3 py-1.5">
                    <select value={e.vertical ?? r.vertical ?? ""}
                      onChange={(ev) => setEdit(r.id, "vertical", ev.target.value)}
                      data-testid={`vertical-select-${r.id}`}
                      className={`h-7 px-1 w-full text-[11px] border rounded ${e.vertical ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`}>
                      <option value="">—</option>
                      {verticals.map((v) => <option key={v.name} value={v.name}>{v.display_name}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    {(s.brand || s.vertical) ? (
                      <button onClick={() => applySuggestion(r)} data-testid={`apply-suggestion-${r.id}`}
                        className="text-[10px] px-2 py-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100">
                        {[s.brand, s.vertical && s.vertical.replace(/_/g, " ")].filter(Boolean).join(" · ")}
                      </button>) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>);
            })}
          </tbody>
        </table>
      </div>
      <datalist id="brand-options">
        {brands.map((b) => <option key={b.id} value={b.name} />)}
      </datalist>
    </div>
  );
}
